import { Dropdown } from '@eventbrite/legacy-eds.containers';
import {
    clearNavigatorRecentSearchTerms,
    getNavigatorLastRecentSearchTerms,
} from '@eventbrite/consumer-search.personalization';
import React, { useCallback, useEffect, useState } from 'react';
import { CompletionType, DEFAULT_COMPLETION_TYPES } from '../../../api/searchAutocomplete';
import { SearchQuerySuggestions } from '../../../containers/SearchSuggestions';
import { useSearchInputContext } from '../../../context';
import { useSearchAutocompleteQuery } from '../../../hooks';
import { SearchAutocompleteResponse } from '../../../types';

import RecentSearches from './RecentSearches/RecentSearches';
import styles from './SearchDropdown.module.scss';
import TrendingSearches from './TrendingSearches/TrendingSearches';

type Props = {
    onClickOutside: (e: React.MouseEvent<HTMLElement>) => void;
};

const getDropdownContent = ({
    completionTypes,
    autocompleteData,
    handleClearRecentSearches,
    recentSearches,
    query,
}: {
    completionTypes: CompletionType[];
    autocompleteData?: SearchAutocompleteResponse;
    handleClearRecentSearches: () => void;
    recentSearches: string[];
    query?: string;
}) => {
    const hasAutocompleteData =
        !!autocompleteData?.query.length || !!autocompleteData?.organizer?.length;

    if (query && hasAutocompleteData) {
        return <SearchQuerySuggestions completionTypes={completionTypes} />;
    } else if (query && !hasAutocompleteData) {
        return null;
    } else if (recentSearches.length > 0) {
        return (
            <RecentSearches
                recentSearches={recentSearches}
                handleClearRecentSearches={handleClearRecentSearches}
            />
        );
    }

    return <TrendingSearches />;
};

const SearchDropdown: React.FunctionComponent<Props> = ({ onClickOutside }) => {
    const { completionTypes = DEFAULT_COMPLETION_TYPES } = useSearchInputContext();
    const [contentHeight, setContentHeight] = useState<string>('inherit');
    const [recentSearches, setRecentSearches] = useState(getNavigatorLastRecentSearchTerms(5));

    const handleClearRecentSearches = React.useCallback(() => {
        clearNavigatorRecentSearchTerms();
        setRecentSearches([]);
    }, [setRecentSearches]);

    const { query } = useSearchInputContext();
    const { data: autocompleteData } = useSearchAutocompleteQuery({
        completionTypes,
    });

    const onResizeHandler = useCallback(() => {
        if (!window?.visualViewport) return setContentHeight('inherit');

        const visualHeight = `calc(${window.visualViewport.height}px - 210px)`;
        setContentHeight(visualHeight);
    }, []);

    useEffect(() => {
        window.visualViewport?.addEventListener('resize', onResizeHandler);
        return () => {
            window.visualViewport?.removeEventListener('resize', onResizeHandler);
        };
    }, [onResizeHandler]);

    const content = React.useMemo(
        () =>
            getDropdownContent({
                completionTypes,
                autocompleteData: autocompleteData || undefined,
                handleClearRecentSearches,
                recentSearches,
                query,
            }),
        [autocompleteData, completionTypes, handleClearRecentSearches, query, recentSearches],
    );

    if (!content) {
        return null;
    }

    return (
        <Dropdown onClickOutside={onClickOutside}>
            <div className={styles.searchTakeoverWrapper} data-testid="searchbar-dropdown">
                <div className={styles.searchTakeoverContent} style={{ height: contentHeight }}>
                    {content}
                </div>
            </div>
        </Dropdown>
    );
};

export default SearchDropdown;
